import { Decimal, DEMO_ENTITY } from '@paguz/contracts';
import { Badge, Button, cn, toast } from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import {
  ArrowLeftIcon,
  BotIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CpuIcon,
  HandIcon,
  OctagonXIcon,
  SendIcon,
  ShieldIcon,
  UndoIcon,
  UserIcon,
  UserRoundCogIcon,
  WrenchIcon,
} from 'lucide-react';
import { useState } from 'react';
import type { AdminAgentTrace, TurnTrace } from '../../api/agent-schemas.ts';
import { describeError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { ConfirmActionDialog } from '../../components/confirm-dialog.tsx';
import { Textarea } from '../../components/form-controls.tsx';
import {
  CopyValue,
  DataList,
  ErrorState,
  JsonBlock,
  LoadingRows,
  PageHeader,
  Panel,
} from '../../components/page.tsx';
import { formatDateTime, formatDuration, formatInt, formatTimeMs } from '../../lib/format.ts';
import { useCan } from '../../lib/session.ts';
import { AGENT_LABEL, GUARD_OUTCOME, SESSION_STATUS } from './labels.ts';
import { MemoryPanel } from './memory-panel.tsx';

const route = getRouteApi('/app/agentes/sesiones/$sessionId');

export function SessionPage() {
  const { sessionId } = route.useParams();
  const query = useQuery({
    queryKey: queryKeys.agentTrace(sessionId),
    queryFn: () => api.getAgentTrace(sessionId),
    refetchInterval: (q) => (q.state.data?.session.live ? 4_000 : false),
  });
  return (
    <>
      <Link
        to="/agentes"
        className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        <ArrowLeftIcon className="size-4" /> Agentes
      </Link>
      {query.isPending ? (
        <LoadingRows rows={10} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <SessionDetail trace={query.data} />
      )}
    </>
  );
}

function SessionDetail({ trace }: { trace: AdminAgentTrace }) {
  const s = trace.session;
  const canOperate = useCan('agents:operate');
  const queryClient = useQueryClient();
  const [killing, setKilling] = useState(false);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.agentTrace(s.id) });
    void queryClient.invalidateQueries({ queryKey: ['agent-sessions'] });
  };
  const takeover = useMutation({
    mutationFn: () => api.takeOverSession(s.id),
    onSuccess: () => {
      refresh();
      toast.success('Tomaste el control: el agente queda en pausa');
    },
    onError: (e) => toast.error(describeError(e)),
  });
  const release = useMutation({
    mutationFn: () => api.releaseSession(s.id),
    onSuccess: () => {
      refresh();
      toast.success('Conversación devuelta al agente');
    },
    onError: (e) => toast.error(describeError(e)),
  });
  const kill = useMutation({
    mutationFn: (reason: string) => api.killSession(s.id, reason),
    onSuccess: () => {
      refresh();
      toast.success('Sesión terminada');
    },
  });
  const takenOver = s.status === 'TAKEN_OVER';
  const closed = s.status === 'CLOSED';
  const calls = trace.turns.flatMap((t) => t.llmCalls);

  return (
    <>
      <PageHeader
        eyebrow={<CopyValue value={s.id} label="ID de la sesión" />}
        title={`${AGENT_LABEL[s.agent]} · ${s.customer?.fullName ?? 'sin cliente'}`}
        description={`${s.channel} · iniciada ${formatDateTime(s.startedAt)} · ${s.turns} turnos del cliente`}
        actions={
          <>
            <Badge variant={SESSION_STATUS[s.status].tone} className="text-sm">
              {SESSION_STATUS[s.status].label}
            </Badge>
            {canOperate && !closed && s.agent !== 'ANALYST_COPILOT' && (
              <>
                {takenOver ? (
                  <Button
                    variant="outline"
                    disabled={release.isPending}
                    onClick={() => {
                      release.mutate();
                    }}
                  >
                    <UndoIcon /> Devolver al agente
                  </Button>
                ) : (
                  <Button
                    variant="highlight"
                    disabled={takeover.isPending}
                    onClick={() => {
                      takeover.mutate();
                    }}
                  >
                    <HandIcon /> Tomar control
                  </Button>
                )}
                <Button
                  variant="destructive"
                  onClick={() => {
                    setKilling(true);
                  }}
                >
                  <OctagonXIcon /> Terminar sesión
                </Button>
              </>
            )}
          </>
        }
      />

      <Panel className="mb-6">
        <DataList
          columns={4}
          items={[
            { label: 'Costo total', value: <span className="font-semibold">USD {s.costUsd}</span> },
            {
              label: 'Llamadas al modelo',
              value: calls.length,
              hint: `${formatInt(s.tokens.input)} entrada · ${formatInt(s.tokens.output)} salida`,
            },
            {
              label: 'Caché',
              value: `${formatInt(s.tokens.cacheRead)} leídos`,
              hint: `${formatInt(s.tokens.cacheWrite)} escritos`,
            },
            { label: 'Guardas disparadas', value: s.guardsTriggered },
            ...(s.customer
              ? [
                  {
                    label: 'Cliente',
                    value: (
                      <Link
                        to="/clientes/$customerId"
                        params={{ customerId: s.customer.id }}
                        className="font-semibold text-primary hover:underline"
                      >
                        {s.customer.fullName}
                      </Link>
                    ),
                  },
                ]
              : []),
            ...(s.applicationId
              ? [
                  {
                    label: 'Expediente',
                    value: (
                      <Link
                        to="/expedientes/$applicationId"
                        params={{ applicationId: s.applicationId }}
                        className="font-semibold text-primary hover:underline"
                      >
                        Ver expediente
                      </Link>
                    ),
                  },
                ]
              : []),
            { label: 'traceId', value: <CopyValue value={trace.traceId} /> },
            ...(s.takenOverBy ? [{ label: 'Control humano', value: s.takenOverBy }] : []),
          ]}
        />
      </Panel>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        <div className="flex flex-col gap-6">
          <Conversation trace={trace} canWrite={canOperate && takenOver} />
          {s.customer && <MemoryPanel customerId={s.customer.id} />}
        </div>
        <TracePanel turns={trace.turns} />
      </div>

      <ConfirmActionDialog
        open={killing}
        onOpenChange={setKilling}
        title="Terminar la sesión (kill switch)"
        description="El agente deja de responder en esta conversación y no admite nuevas respuestas. El cliente puede abrir una sesión nueva."
        destructive
        confirmWord={s.id}
        confirmLabel="Terminar sesión"
        onConfirm={async (reason) => {
          await kill.mutateAsync(reason);
        }}
      />
    </>
  );
}

function Conversation({ trace, canWrite }: { trace: AdminAgentTrace; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const send = useMutation({
    mutationFn: () => api.sendHumanMessage(trace.session.id, text.trim()),
    onSuccess: () => {
      setText('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentTrace(trace.session.id) });
    },
    onError: (e) => toast.error(describeError(e)),
  });
  return (
    <Panel
      title="Conversación"
      description={`Como la ve el cliente. Los mensajes humanos aparecen como “Asesor ${DEMO_ENTITY.name}”.`}
    >
      <ol className="flex flex-col gap-3">
        {trace.transcript.map((m) =>
          m.role === 'system_note' ? (
            <li
              key={m.seq}
              className="rounded-xl bg-muted px-3 py-2 text-center text-xs text-muted-foreground"
            >
              {m.text}
            </li>
          ) : (
            <li key={m.seq} className={cn('flex gap-2', m.role === 'user' && 'flex-row-reverse')}>
              <span className="mt-1 text-muted-foreground">
                {m.role === 'user' ? (
                  <UserIcon className="size-4" />
                ) : m.role === 'human_agent' ? (
                  <UserRoundCogIcon className="size-4 text-highlight" />
                ) : (
                  <BotIcon className="size-4" />
                )}
              </span>
              <div
                className={cn(
                  'max-w-[85%] rounded-2xl px-3.5 py-2 text-sm',
                  m.role === 'user'
                    ? 'bg-highlight-soft text-highlight-soft-foreground'
                    : m.role === 'human_agent'
                      ? 'border border-highlight bg-card'
                      : 'bg-muted',
                )}
              >
                {m.role === 'human_agent' && (
                  <p className="text-xs font-semibold text-highlight">
                    {m.author ?? `Asesor ${DEMO_ENTITY.name}`}
                  </p>
                )}
                <p>{m.text}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  #{m.seq} · {formatTimeMs(m.at).slice(0, 8)}
                  {m.inputMode === 'VOICE' && ' · voz'}
                  {m.inputMode === 'FILE' && ' · archivo'}
                </p>
              </div>
            </li>
          ),
        )}
      </ol>
      {canWrite && (
        <form
          className="mt-4 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            send.mutate();
          }}
        >
          <Textarea
            aria-label="Mensaje al cliente"
            placeholder={`Escribe al cliente como Asesor ${DEMO_ENTITY.name}…`}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
            }}
            className="min-h-16"
          />
          <Button type="submit" variant="highlight" disabled={!text.trim() || send.isPending}>
            <SendIcon /> Enviar como Asesor {DEMO_ENTITY.name}
          </Button>
        </form>
      )}
    </Panel>
  );
}

function TracePanel({ turns }: { turns: readonly TurnTrace[] }) {
  const [open, setOpen] = useState<number | null>(turns.at(-1)?.seq ?? null);
  return (
    <Panel
      title="Traza por turno"
      description="Llamadas al modelo, herramientas (argumentos y resultado), guardas, skills y memoria."
      bodyClassName="flex flex-col gap-3"
    >
      {turns.length === 0 && <p className="text-sm text-muted-foreground">Sin turnos todavía.</p>}
      {turns.map((t) => {
        const expanded = open === t.seq;
        const cost = t.llmCalls.reduce((a, c) => a.plus(c.costUsd), new Decimal(0)).toFixed(4);
        const flagged = t.guards.filter((g) => g.outcome !== 'PASS').length;
        return (
          <section key={t.seq} className="rounded-xl border border-border">
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => {
                setOpen(expanded ? null : t.seq);
              }}
              className="flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left"
            >
              {expanded ? (
                <ChevronDownIcon className="size-4" />
              ) : (
                <ChevronRightIcon className="size-4" />
              )}
              <span className="font-semibold">Turno #{t.seq}</span>
              <span className="text-sm text-muted-foreground">
                {formatTimeMs(t.startedAt).slice(0, 8)} · {formatDuration(t.durationMs)}
              </span>
              <Badge variant="outline">
                <CpuIcon /> {t.llmCalls.length} LLM
              </Badge>
              <Badge variant="outline">
                <WrenchIcon /> {t.tools.length} herramientas
              </Badge>
              {flagged > 0 && (
                <Badge variant="warning">
                  <ShieldIcon /> {flagged} guardas
                </Badge>
              )}
              <span className="ml-auto text-sm tabular-nums text-muted-foreground">USD {cost}</span>
            </button>
            {expanded && <TurnDetail turn={t} />}
          </section>
        );
      })}
    </Panel>
  );
}

function TurnDetail({ turn }: { turn: TurnTrace }) {
  return (
    <div className="flex flex-col gap-4 border-t border-border px-4 py-4">
      <div>
        <p className="mb-1.5 text-xs font-semibold text-muted-foreground">Llamadas al modelo</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1 pr-3 font-semibold">Rol</th>
                <th className="py-1 pr-3 font-semibold">Modelo · esfuerzo</th>
                <th className="py-1 pr-3 text-right font-semibold">Entrada</th>
                <th className="py-1 pr-3 text-right font-semibold">Salida</th>
                <th className="py-1 pr-3 text-right font-semibold">Caché (leído / escrito)</th>
                <th className="py-1 pr-3 text-right font-semibold">Latencia</th>
                <th className="py-1 pr-3 font-semibold">Fin</th>
                <th className="py-1 text-right font-semibold">USD</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {turn.llmCalls.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="py-1.5 pr-3">{AGENT_LABEL[c.agentRole]}</td>
                  <td className="py-1.5 pr-3 font-mono">
                    {c.model} · {c.effort}
                  </td>
                  <td className="py-1.5 pr-3 text-right">{formatInt(c.inputTokens)}</td>
                  <td className="py-1.5 pr-3 text-right">{formatInt(c.outputTokens)}</td>
                  <td className="py-1.5 pr-3 text-right">
                    {formatInt(c.cacheReadTokens)} / {formatInt(c.cacheWriteTokens)}
                  </td>
                  <td className="py-1.5 pr-3 text-right">{formatDuration(c.latencyMs)}</td>
                  <td className="py-1.5 pr-3 font-mono">{c.stopReason ?? '—'}</td>
                  <td className="py-1.5 text-right">{c.costUsd}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {turn.tools.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-muted-foreground">Herramientas</p>
          {turn.tools.map((tool) => (
            <details key={tool.id} className="rounded-lg border border-border px-3 py-2">
              <summary className="flex cursor-pointer flex-wrap items-center gap-2 text-sm">
                <code className="font-mono font-semibold">{tool.toolName}</code>
                <Badge variant="outline">{tool.effect}</Badge>
                <Badge
                  variant={
                    tool.status === 'OK'
                      ? 'success'
                      : tool.status === 'HITL_REQUIRED'
                        ? 'highlight'
                        : 'danger'
                  }
                >
                  {tool.status}
                </Badge>
                {tool.guardOutcome && (
                  <Badge variant={GUARD_OUTCOME[tool.guardOutcome]}>
                    guarda {tool.guardOutcome}
                  </Badge>
                )}
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatDuration(tool.durationMs)}
                </span>
              </summary>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                <div>
                  <p className="mb-1 text-xs text-muted-foreground">Argumentos</p>
                  <JsonBlock value={tool.input} />
                </div>
                <div>
                  <p className="mb-1 text-xs text-muted-foreground">Resultado</p>
                  <JsonBlock value={tool.output} />
                </div>
              </div>
            </details>
          ))}
        </div>
      )}
      {turn.guards.length > 0 && (
        <div>
          <p className="mb-1.5 text-xs font-semibold text-muted-foreground">Guardas</p>
          <ul className="flex flex-col gap-1 text-sm">
            {turn.guards.map((g, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2">
                <Badge variant={GUARD_OUTCOME[g.outcome]}>{g.outcome}</Badge>
                <code className="font-mono text-xs">
                  {g.stage} · {g.guard}
                </code>
                {Object.keys(g.details).length > 0 && (
                  <span className="text-xs text-muted-foreground">{JSON.stringify(g.details)}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      <DataList
        columns={3}
        items={[
          { label: 'Skills cargadas', value: turn.skills.length ? turn.skills.join(', ') : '—' },
          {
            label: 'Memoria leída',
            value: turn.memory.read.length ? turn.memory.read.join(', ') : '—',
          },
          {
            label: 'Memoria escrita',
            value: turn.memory.written.length
              ? turn.memory.written.map((w) => `${w.key} = ${w.value} (${w.status})`).join('; ')
              : '—',
            hint: turn.checkpoint ? `Checkpoint ${turn.checkpoint}` : undefined,
          },
        ]}
      />
    </div>
  );
}
